import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { MessagesSquare, BookOpen, Hexagon, ArrowUpRight, Award, Clock, MapPin } from "lucide-react";

const pub = axios.create({ baseURL: API });

const ROTATE_MS = 6000;

export default function FeaturedThisWeek() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    pub.get("/public/featured-this-week")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  // Build the actual slide list from non-null picks
  const slides = (() => {
    if (!data) return [];
    const list = [];
    if (data.thread) list.push({ kind: "thread", ...data.thread });
    if (data.guide) list.push({ kind: "guide", ...data.guide });
    if (data.apiary) list.push({ kind: "apiary", ...data.apiary });
    return list;
  })();

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!data || slides.length === 0) return null;
  const cur = slides[idx];

  return (
    <section className="py-12 bg-gradient-to-br from-amber-50/40 to-stone-50 border-b border-stone-100" data-testid="featured-this-week">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700">Featured this week</div>
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight mt-1">Three picks from the hive</h2>
          </div>
          <div className="flex items-center gap-1.5" data-testid="featured-week-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-amber-500" : "w-1.5 bg-stone-300 hover:bg-stone-400"}`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`featured-week-dot-${i}`}
              />
            ))}
          </div>
        </div>

        <div className="relative" data-testid={`featured-week-slide-${cur.kind}`}>
          {cur.kind === "thread" && <ThreadCard item={cur} />}
          {cur.kind === "guide" && <GuideCard item={cur} />}
          {cur.kind === "apiary" && <ApiaryCard item={cur} />}
        </div>
      </div>
    </section>
  );
}

function CardShell({ icon: Icon, badge, accent, to, children, testid }) {
  return (
    <Link
      to={to}
      className={`block bg-white border border-stone-200 hover:border-amber-300 rounded-2xl p-6 sm:p-8 transition-all hover:shadow-sm group ${accent}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-semibold">{badge}</span>
        <ArrowUpRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-amber-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all ml-auto" />
      </div>
      {children}
    </Link>
  );
}

function ThreadCard({ item }) {
  return (
    <CardShell icon={MessagesSquare} badge="Forum hotpost" to={`/forum/t/${item.slug}`} testid="featured-week-thread">
      <h3 className="font-display text-xl sm:text-2xl font-medium leading-snug">{item.title}</h3>
      <div className="text-sm text-stone-600 mt-2 flex flex-wrap gap-x-3 gap-y-1">
        <span>by {item.author_name}</span>
        <span>· {item.category_name}</span>
        <span>· {item.reply_count} repl{item.reply_count === 1 ? "y" : "ies"}</span>
        {item.score > 0 && <span className="text-amber-700">· +{item.score} upvotes this week</span>}
      </div>
    </CardShell>
  );
}

function GuideCard({ item }) {
  return (
    <CardShell icon={BookOpen} badge={`Seasonal · ${item.level}`} to={`/app/guide/${item.slug}`} testid="featured-week-guide">
      <h3 className="font-display text-xl sm:text-2xl font-medium leading-snug">{item.title}</h3>
      <p className="text-sm text-stone-600 mt-2 line-clamp-2">{item.summary}</p>
      <div className="text-xs text-stone-500 mt-3 inline-flex items-center gap-1">
        <Clock className="w-3 h-3" /> {item.reading_minutes} min read
        <span className="ml-2 text-stone-400">(sign-in required)</span>
      </div>
    </CardShell>
  );
}

function ApiaryCard({ item }) {
  return (
    <CardShell icon={Hexagon} badge="Apiary spotlight" to={`/share/${item.slug}`} testid="featured-week-apiary">
      <h3 className="font-display text-xl sm:text-2xl font-medium leading-snug">{item.owner_name}'s apiary</h3>
      <div className="text-sm text-stone-600 mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {item.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location}</span>}
        <span className="inline-flex items-center gap-1"><Hexagon className="w-3 h-3 text-yellow-500" /> {item.hives_count} hive{item.hives_count === 1 ? "" : "s"}</span>
        {item.badges_count > 0 && <span className="inline-flex items-center gap-1"><Award className="w-3 h-3 text-amber-600" /> {item.badges_count} badge{item.badges_count === 1 ? "" : "s"}</span>}
      </div>
    </CardShell>
  );
}
